import {
  COURT_SIZE_NOTE_MAX_LENGTH,
  RESERVATION_UNIT_OPTIONS,
  type CourtItem,
  type CourtSaveInput,
  type VenueCourtFloorType,
  type VenueCourtStatus,
  type VenueCourtType,
} from './courtTypes'
import { formatAmount } from './format'

/**
 * 코트 추가/수정 폼 상태 ↔ `CourtSaveInput` 매핑 + 사전 검증 + 카드 표시 뷰모델(설계 §2-3).
 *
 * 🚨 `if (status === ...)` 를 컴포넌트에 흩지 않는다 — `venueProfileViewModel.ts` 가 세운 규칙 승계.
 */

/** 숫자 입력은 빈 문자열이 유효한 중간 상태라 텍스트로 다룬다 — 저장 직전에만 숫자로 정규화한다. */
export interface CourtFormState {
  name: string
  floorType: VenueCourtFloorType | ''
  courtType: VenueCourtType | ''
  sizeNote: string
  status: VenueCourtStatus
  /** 고정 4종 select — 항상 유효한 값 하나를 갖는다. */
  reservationUnitMinutes: number
  maxReservationMinutes: string
  weekdayHourlyFee: string
  weekendHourlyFee: string
}

const DEFAULT_RESERVATION_UNIT_MINUTES = RESERVATION_UNIT_OPTIONS[1]?.value ?? 60

/** 신규 등록 폼의 초기값. */
export function emptyCourtFormState(): CourtFormState {
  return {
    name: '',
    floorType: '',
    courtType: '',
    sizeNote: '',
    status: 'OPEN',
    reservationUnitMinutes: DEFAULT_RESERVATION_UNIT_MINUTES,
    maxReservationMinutes: '',
    weekdayHourlyFee: '',
    weekendHourlyFee: '',
  }
}

/** 기존 코트 → 수정 폼 초기값(프리필). 🚨 `courtType`·`sizeNote` 도 프리필해야 무수정 저장에도 값이 유지된다(#618 §3-1). */
export function toCourtFormState(court: CourtItem): CourtFormState {
  return {
    name: court.name,
    floorType: court.floorType ?? '',
    courtType: court.courtType ?? '',
    sizeNote: court.sizeNote ?? '',
    status: court.status,
    reservationUnitMinutes: court.reservationUnitMinutes,
    maxReservationMinutes: String(court.maxReservationMinutes),
    weekdayHourlyFee: String(court.weekdayHourlyFee),
    weekendHourlyFee: String(court.weekendHourlyFee),
  }
}

/** 폼 → `POST`/`PUT` 요청 바디. 검증을 통과한 뒤에만 호출한다(숫자 파싱 실패를 가정하지 않는다). */
export function toCourtSaveInput(form: CourtFormState): CourtSaveInput {
  const trimmedSizeNote = form.sizeNote.trim()
  return {
    name: form.name.trim(),
    floorType: form.floorType === '' ? null : form.floorType,
    courtType: form.courtType === '' ? null : form.courtType,
    sizeNote: trimmedSizeNote.length > 0 ? trimmedSizeNote : null,
    status: form.status,
    reservationUnitMinutes: form.reservationUnitMinutes,
    maxReservationMinutes: Number(form.maxReservationMinutes),
    weekdayHourlyFee: Number(form.weekdayHourlyFee),
    weekendHourlyFee: Number(form.weekendHourlyFee),
  }
}

export const COURT_NAME_MAX_LENGTH = 50

export type CourtFormErrors = Partial<
  Record<'name' | 'weekdayHourlyFee' | 'weekendHourlyFee' | 'maxReservationMinutes' | 'sizeNote', string>
>

/**
 * 클라이언트 사전 검증(#597 §5 검증표 미러링). 서버가 최종 판정자이고, 이 표면은 `fieldErrors` 봉투가
 * 없어(§5) 이름 중복처럼 클라이언트가 미리 알 수 없는 위반은 제출 후 서버 응답으로만 드러난다.
 */
export function validateCourtForm(form: CourtFormState): CourtFormErrors {
  const errors: CourtFormErrors = {}

  const trimmedName = form.name.trim()
  if (trimmedName.length === 0) {
    errors.name = '코트 이름을 입력해 주세요.'
  } else if (trimmedName.length > COURT_NAME_MAX_LENGTH) {
    errors.name = `코트 이름은 최대 ${COURT_NAME_MAX_LENGTH}자까지 입력할 수 있습니다.`
  }

  const weekdayFeeError = validateNonNegativeIntegerFee(form.weekdayHourlyFee, '평일 요금')
  if (weekdayFeeError) errors.weekdayHourlyFee = weekdayFeeError

  const weekendFeeError = validateNonNegativeIntegerFee(form.weekendHourlyFee, '주말 요금')
  if (weekendFeeError) errors.weekendHourlyFee = weekendFeeError

  const maxReservationError = validateMaxReservationMinutes(form.maxReservationMinutes, form.reservationUnitMinutes)
  if (maxReservationError) errors.maxReservationMinutes = maxReservationError

  if (form.sizeNote.trim().length > COURT_SIZE_NOTE_MAX_LENGTH) {
    errors.sizeNote = `규격은 최대 ${COURT_SIZE_NOTE_MAX_LENGTH}자까지 입력할 수 있습니다.`
  }

  return errors
}

function validateNonNegativeIntegerFee(value: string, label: string): string | undefined {
  const trimmed = value.trim()
  if (trimmed.length === 0) return `${label}을(를) 입력해 주세요.`
  if (!/^\d+$/.test(trimmed)) return `${label}은(는) 0 이상의 정수로 입력해 주세요.`
  return undefined
}

function validateMaxReservationMinutes(value: string, unitMinutes: number): string | undefined {
  const trimmed = value.trim()
  if (trimmed.length === 0) return '최대 예약 시간을 입력해 주세요.'
  if (!/^\d+$/.test(trimmed)) return '최대 예약 시간은 분 단위 정수로 입력해 주세요.'
  const minutes = Number(trimmed)
  if (minutes < unitMinutes) return `최대 예약 시간은 예약 단위(${unitMinutes}분) 이상이어야 합니다.`
  if (minutes % unitMinutes !== 0) return `최대 예약 시간은 예약 단위(${unitMinutes}분)의 배수여야 합니다.`
  return undefined
}

export interface CourtCardViewModel {
  /** 🔒 관리자 표면의 요금은 **기본가**다(#852 §4-2) — 구간이 있으면 아래 요약이 함께 나온다. */
  feeText: string
  /** 시간대별 구간이 있을 때만 채워지는 한 줄 요약. 없으면 `null`(카드는 지금과 동일하게 그려진다). */
  priceBandSummaryText: string | null
  operatingHoursText: string
}

const OPERATING_HOURS_NOT_SET_TEXT = '운영시간 설정 필요'

/**
 * 시간대별 요금 구간 요약(#852 §4-2 가법 필드). 필드가 없거나 구간이 0개면 `null` —
 * 서버가 아직 이 필드를 안 내려주는 배포 시점에도 카드가 지금과 똑같이 그려진다(설계 R4).
 * 🚨 구간 문구는 서버 `timeRangeLabel` 을 그대로 쓴다(화면이 시각을 조립하지 않는다).
 */
function toPriceBandSummaryText(court: CourtItem): string | null {
  const groups = (court.priceBands ?? []).filter((group) => group.bands.length > 0)
  if (groups.length === 0) return null

  return groups
    .map(
      (group) =>
        `${group.dayTypeLabel} ${group.bands
          .map((band) => `${band.timeRangeLabel} ${formatAmount(band.hourlyFee)}`)
          .join(', ')}`
    )
    .join(' · ')
}

/** 카드 표시용 파생값. 상태·예약단위·최대예약시간·바닥재질은 서버 라벨을 그대로 쓴다(조립하지 않음). */
export function toCourtCardViewModel(court: CourtItem): CourtCardViewModel {
  return {
    feeText: `평일 ${formatAmount(court.weekdayHourlyFee)} / 주말 ${formatAmount(court.weekendHourlyFee)}`,
    priceBandSummaryText: toPriceBandSummaryText(court),
    operatingHoursText:
      court.operatingStartTime && court.operatingEndTime
        ? `${court.operatingStartTime} ~ ${court.operatingEndTime}`
        : OPERATING_HOURS_NOT_SET_TEXT,
  }
}
